import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, Shield } from "lucide-react";
import LegalFooter from "@/components/app/LegalFooter";

const LOGO = "https://customer-assets.emergentagent.com/job_pitch-connect-40/artifacts/yd54d9mb_D6EC86DD-43FE-4578-97EA-A54FBCABC788.png";
const HERO_BG = "https://images.pexels.com/photos/37578663/pexels-photo-37578663.png";

export default function Landing() {
  // If someone lands here via a share card (?ref=xyz), stash the referral code
  // in localStorage so the eventual /register call can attribute the signup.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && /^[a-z0-9]{4,24}$/i.test(ref)) {
      try { localStorage.setItem("fm_ref", ref.toLowerCase()); } catch { /* ignore quota */ }
    }
  }, []);
  return (
    <div className="relative min-h-screen bg-footy-bg text-white overflow-hidden grain">
      {/* Hero background */}
      <div className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-footy-bg/60 via-footy-bg/85 to-footy-bg" />
      </div>

      {/* Nav */}
      <header className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-5 py-5">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Footymatch" className="w-11 h-11 rounded-full" />
          <span className="font-display text-2xl tracking-wider">FOOTYMATCH</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login-btn" className="text-slate-300 hover:text-white font-semibold text-sm">Log in</Link>
          <Link to="/register" data-testid="nav-signup-btn" className="footy-btn-primary !py-2.5 !px-6 !text-sm">Sign up</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 pt-10 md:pt-20 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-footy-red uppercase tracking-[0.25em] text-xs font-bold mb-4">For British football fans — dating, mates & everything in between</p>
          <h1 className="font-display text-6xl sm:text-7xl md:text-8xl leading-[0.9] tracking-tight uppercase">
            Bringing<br/>
            <span className="text-footy-red">football fans</span><br/>
            together.
          </h1>
          <p className="mt-4 font-display text-2xl uppercase tracking-wider text-slate-200">
            Find fellow supporters of your club.
          </p>
          <p className="mt-4 text-lg text-slate-300 max-w-lg leading-relaxed">
            Dating, friendship, or just someone to text about the goal. Going to games isn&apos;t required — armchair loyal counts just as much.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-cta-signup" className="footy-btn-primary">Find your match</Link>
            <Link to="/login" data-testid="hero-cta-login" className="footy-btn-secondary">I have an account</Link>
          </div>
          <p className="mt-6 text-xs uppercase tracking-widest text-slate-500">
            Same passion. Same values. Better connections.
          </p>
        </div>
        <div className="relative">
          <img src={LOGO} alt="Footymatch logo" className="w-full max-w-md mx-auto drop-shadow-[0_20px_60px_rgba(230,57,70,0.35)]" />
        </div>
      </section>

      {/* Feature strip */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Users, title: "Same-club fans first", copy: "Discover shows fellow supporters of your club before anyone else." },
            { icon: Calendar, title: "Matchday mates", copy: "Never travel to a game alone again — find a buddy for kickoff." },
            { icon: Heart, title: "Every kind of fan", copy: "Armchair loyal, home games only, or home & away — all welcome." },
            { icon: Shield, title: "Inclusive & safe", copy: "All identities welcome. Respect on and off the pitch." },
          ].map(({ icon: Icon, title, copy }) => (
            <div key={title} className="footy-card p-6 hover:bg-footy-cardHover transition-colors">
              <div className="w-11 h-11 rounded-xl bg-footy-red/15 text-footy-red flex items-center justify-center mb-4">
                <Icon size={22} />
              </div>
              <h3 className="font-display text-2xl uppercase tracking-wide">{title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-display text-2xl uppercase tracking-wider text-center sm:text-left">Because the right match is <span className="text-footy-red">more than just 90 minutes</span>.</p>
          <Link to="/register" data-testid="footer-cta-signup" className="footy-btn-primary">Join Footymatch</Link>
        </div>
      </section>
      <LegalFooter />
    </div>
  );
}
